import { useState, useRef, useCallback, useEffect } from 'react'
import { useQuery, useMutation, usePaginatedQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Button } from '@/components/ui/button'
import { Link, useNavigate } from 'react-router-dom'
import { Search, Loader2, ArrowUpDown, ChevronRight as ChevronRightSmall, Trash2, Play, Clock, AlertCircle, Film, Clapperboard, Mic, Type, Users, Monitor, Smartphone, Globe, RefreshCw, Download, Volume2, Star, X, Filter as FilterIcon } from 'lucide-react'

const PLAN_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  admin: { bg: 'bg-red-100', text: 'text-red-700', label: 'Admin' },
  free: { bg: 'bg-neutral-100', text: 'text-neutral-600', label: 'Free' },
  pro: { bg: 'bg-green-100', text: 'text-green-700', label: 'Pro' },
  max: { bg: 'bg-violet-100', text: 'text-violet-700', label: 'Max' },
  ultra: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', label: 'Ultra' },
  'pro-trialing': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Pro (Trial)' },
  'pro-past_due': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pro (Past Due)' },
  'max-past_due': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Max (Past Due)' },
  'ultra-past_due': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Ultra (Past Due)' },
  'pro-incomplete': { bg: 'bg-red-100', text: 'text-red-700', label: 'Pro (Incomplete)' },
  'max-incomplete': { bg: 'bg-red-100', text: 'text-red-700', label: 'Max (Incomplete)' },
  'ultra-incomplete': { bg: 'bg-red-100', text: 'text-red-700', label: 'Ultra (Incomplete)' },
}

function PlanBadge({ plan }: { plan: string }) {
  const style = PLAN_STYLES[plan] ?? PLAN_STYLES.free
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  )
}

type Filter = 'all' | 'hasClips' | 'completed' | 'hasScript' | 'hasCharacters' | 'hasImages' | 'hasGenerated' | 'hasExported' | 'hasFeedback'
type SortOrder = 'asc' | 'desc'

const CONTENT_FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All Content' },
  { value: 'hasScript', label: 'Has Script' },
  { value: 'hasCharacters', label: 'Has Characters' },
  { value: 'hasImages', label: 'Has Ref Images' },
  { value: 'hasClips', label: 'Has Clips' },
  { value: 'hasGenerated', label: 'Generated' },
  { value: 'hasExported', label: 'Exported' },
  { value: 'hasFeedback', label: 'Has Feedback' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'Any Status' },
  { value: 'narrative', label: 'Narrative' },
  { value: 'characters', label: 'Characters' },
  { value: 'script', label: 'Script' },
  { value: 'scenes', label: 'Scenes' },
  { value: 'storyboard', label: 'Storyboard' },
  { value: 'generating', label: 'Generating' },
  { value: 'video', label: 'Video' },
  { value: 'preview', label: 'Preview' },
  { value: 'completed', label: 'Complete' },
]

const SOURCE_OPTIONS = [
  { value: '', label: 'Any Source' },
  { value: 'web', label: 'Web' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'cli', label: 'CLI' },
]

const PLAN_OPTIONS = [
  { value: '', label: 'Any Plan' },
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
  { value: 'max', label: 'Max' },
  { value: 'ultra', label: 'Ultra' },
  { value: 'admin', label: 'Admin' },
]

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

function formatSessionTime(minutes: number): string {
  if (minutes < 1) return '<1m'
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  script: { label: 'Script', color: 'text-blue-600 bg-blue-50' },
  scenes: { label: 'Scenes', color: 'text-violet-600 bg-violet-50' },
  generating: { label: 'Generating', color: 'text-amber-600 bg-amber-50' },
  preview: { label: 'Preview', color: 'text-emerald-600 bg-emerald-50' },
  completed: { label: 'Complete', color: 'text-emerald-700 bg-emerald-50' },
  narrative: { label: 'Narrative', color: 'text-blue-600 bg-blue-50' },
  characters: { label: 'Characters', color: 'text-blue-600 bg-blue-50' },
  storyboard: { label: 'Storyboard', color: 'text-violet-600 bg-violet-50' },
  video: { label: 'Video', color: 'text-amber-600 bg-amber-50' },
}

const MODEL_LABELS: Record<string, string> = {
  'kling-v2.1': 'Kling 2.1',
  'kling-v2': 'Kling 2',
  'kling-v1.6': 'Kling 1.6',
  'minimax-video-01': 'MiniMax',
  'wan-2.1': 'Wan 2.1',
  'seedance-1.0': 'Seedance',
  'pika-2.2': 'Pika 2.2',
  'veo-2': 'Veo 2',
}

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500',
]

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return name[0].toUpperCase()
  }
  if (email) return email[0].toUpperCase()
  return '?'
}

function UserAvatar({ name, email, avatarUrl, userId, size = 'md' }: {
  name?: string
  email?: string
  avatarUrl?: string
  userId?: string
  size?: 'sm' | 'md'
}) {
  const navigate = useNavigate()
  const sizeClass = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs'
  const initials = getInitials(name, email)
  const color = getAvatarColor(name || email || '')

  const handleClick = (e: React.MouseEvent) => {
    if (!userId) return
    e.preventDefault()
    e.stopPropagation()
    navigate(`/admin/users/${userId}`)
  }

  const avatar = avatarUrl ? (
    <img
      src={avatarUrl}
      alt=""
      className={`${sizeClass} rounded-full object-cover shrink-0 ${userId ? 'cursor-pointer ring-2 ring-transparent hover:ring-blue-300 transition-all' : ''}`}
      onClick={handleClick}
      referrerPolicy="no-referrer"
    />
  ) : (
    <div
      className={`${sizeClass} rounded-full ${color} flex items-center justify-center text-white font-semibold shrink-0 ${userId ? 'cursor-pointer ring-2 ring-transparent hover:ring-blue-300 transition-all' : ''}`}
      onClick={handleClick}
    >
      {initials}
    </div>
  )

  return avatar
}

function Stat({ icon: Icon, value, label, color }: { icon: any; value: string | number; label: string; color?: string }) {
  return (
    <div className="flex items-center gap-1.5" title={label}>
      <Icon className={`h-3 w-3 ${color || 'text-neutral-400'}`} />
      <span className="text-[11px] tabular-nums text-neutral-600 font-medium">{value}</span>
    </div>
  )
}

function MiniBar({ filled, total, color }: { filled: number; total: number; color: string }) {
  if (total === 0) return null
  const pct = Math.round((filled / total) * 100)
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-16 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-neutral-400">{pct}%</span>
    </div>
  )
}

function FilterSelect({ value, onChange, options, label }: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  label: string
}) {
  const isActive = value !== '' && value !== 'all'
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`text-xs px-2.5 py-1.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-neutral-400 transition-colors ${
        isActive
          ? 'border-neutral-900 text-neutral-900 font-medium'
          : 'border-neutral-200 text-neutral-500'
      }`}
      title={label}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}

function ActiveFilterPill({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-neutral-900 text-white">
      {label}
      <button onClick={onClear} className="hover:bg-white/20 rounded-full p-0.5 transition-colors">
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  )
}

function ProjectCard({ project, confirmDeleteId, setConfirmDeleteId, deleteProject, feedback, onUserClick }: {
  project: any
  confirmDeleteId: string | null
  setConfirmDeleteId: (id: string | null) => void
  deleteProject: any
  feedback?: { rating: number; feedback: string }
  onUserClick?: (userId: string) => void
}) {
  const navigate = useNavigate()
  const statusCfg = STATUS_CONFIG[project.status] || STATUS_CONFIG.script
  const modelLabel = project.videoModel ? (MODEL_LABELS[project.videoModel] || project.videoModel) : null
  const sourceIcon = project.source === 'desktop' ? Monitor : project.source === 'cli' ? Smartphone : Globe

  const handleUserClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onUserClick && project.userId) {
      onUserClick(project.userId)
    } else if (project.userId) {
      navigate(`/admin/users/${project.userId}`)
    }
  }

  return (
    <Link
      to={`/admin/videos/inspect/${project.shortcode ?? project._id}`}
      className="block rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm transition-all"
    >
      <div className="flex gap-4 p-4">
        {/* Thumbnail */}
        {project.thumbnailUrl ? (
          <img
            src={project.thumbnailUrl}
            alt=""
            className="w-[140px] h-[88px] rounded-lg object-cover shrink-0 bg-neutral-100"
          />
        ) : (
          <div className="w-[140px] h-[88px] rounded-lg bg-neutral-100 flex items-center justify-center shrink-0">
            <Film className="h-6 w-6 text-neutral-300" />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Title row */}
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-semibold text-neutral-900 truncate">
              {project.title || 'Untitled Video'}
            </p>
            {project.shortcode && (
              <span className="text-[10px] font-mono text-neutral-400 shrink-0">{project.shortcode}</span>
            )}
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${statusCfg.color} shrink-0`}>
              {statusCfg.label}
            </span>
            {project.hasExported && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 flex items-center gap-0.5 shrink-0">
                <Download className="h-3 w-3" />
                Exported
              </span>
            )}
            {project.mode === 'director' && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-700 font-medium shrink-0">Director</span>}
          </div>

          {/* User row */}
          <div className="flex items-center gap-2.5 mt-1.5">
            <UserAvatar
              name={project.userName}
              email={project.userEmail}
              avatarUrl={project.userAvatarUrl}
              userId={project.userId}
              size="sm"
            />
            <button
              onClick={handleUserClick}
              className="text-xs text-neutral-600 hover:text-blue-600 hover:underline transition-colors truncate"
            >
              {project.userName || project.userEmail || 'Unknown user'}
            </button>
            <PlanBadge plan={project.effectivePlan || 'free'} />
            <span className="text-[10px] text-neutral-400 shrink-0 inline-flex items-center gap-0.5" title={`Source: ${project.source}`}>
              {(() => { const I = sourceIcon; return <I className="h-3 w-3" /> })()}
            </span>
            {project.isDesktop && <span className="text-[10px] px-1 py-0.5 rounded bg-indigo-50 text-indigo-600 font-medium shrink-0">Desktop</span>}
            {project.isNux && <span className="text-[10px] px-1 py-0.5 rounded bg-pink-50 text-pink-600 font-medium shrink-0">NUX</span>}
          </div>

          {/* Narrative preview */}
          {project.narrativePreview && (
            <p className="mt-1.5 text-[11px] text-neutral-400 truncate">
              {project.narrativePreview}
            </p>
          )}

          {/* Stats row */}
          <div className="mt-auto pt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <Stat icon={Clock} value={formatSessionTime(project.sessionMinutes)} label="Session duration" />
            <Stat icon={Film} value={project.clipCount} label="Total clips" />
            {project.clipCount > 0 && (
              <div className="flex items-center gap-1.5" title={`${project.generatedClipCount}/${project.clipCount} clips generated`}>
                <Clapperboard className="h-3 w-3 text-emerald-400" />
                <span className="text-[11px] tabular-nums text-neutral-600 font-medium">{project.generatedClipCount}</span>
                <MiniBar filled={project.generatedClipCount} total={project.clipCount} color="bg-emerald-400" />
              </div>
            )}
            {project.failedClipCount > 0 && <Stat icon={AlertCircle} value={project.failedClipCount} label="Failed clips" color="text-red-400" />}
            {project.totalTimelineDuration > 0 && (
              <Stat icon={Play} value={formatDuration(project.totalTimelineDuration)} label="Timeline duration" color="text-blue-400" />
            )}
            {project.totalIterations > 0 && <Stat icon={RefreshCw} value={project.totalIterations} label="Regenerations" color="text-amber-400" />}
            {project.characterCount > 0 && (
              <div className="flex items-center gap-1" title={project.characterNames?.join(', ')}>
                <Users className="h-3 w-3 text-neutral-400" />
                <span className="text-[11px] tabular-nums text-neutral-600 font-medium">{project.characterCount}</span>
              </div>
            )}
            {project.ttsClipCount > 0 && <Stat icon={Mic} value={project.ttsClipCount} label="TTS clips" color="text-pink-400" />}
            {project.audioTrackCount > 0 && <Stat icon={Volume2} value={project.audioTrackCount} label="Audio tracks" color="text-orange-400" />}
            {project.textOverlayCount > 0 && <Stat icon={Type} value={project.textOverlayCount} label="Text overlays" color="text-sky-400" />}
          </div>

          {/* Bottom metadata */}
          <div className="mt-1.5 flex items-center gap-3 text-[10px] text-neutral-400">
            <span>{project.aspectRatio}</span>
            {modelLabel && <span>{modelLabel}</span>}
            {project.generationModes?.length > 0 && (
              <span className="truncate max-w-[200px]">{project.generationModes.join(', ')}</span>
            )}
            {project.hasTts && <span className="text-pink-400">TTS</span>}
            {project.hasNarratorVoice && <span className="text-indigo-400">Narrator</span>}
            {project.hasCompaction && <span className="text-amber-400">Compacted</span>}
            {project.activeSkills?.length > 0 && (
              <span className="text-violet-400 truncate max-w-[150px]">{project.activeSkills.join(', ')}</span>
            )}
            <span className="ml-auto shrink-0">{timeAgo(project.lastActivityAt)}</span>
          </div>
        </div>

        {/* Right actions */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          {feedback && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 flex items-center gap-0.5">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              {feedback.rating}
            </span>
          )}
          {confirmDeleteId === project._id ? (
            <div className="flex items-center gap-1" onClick={(e) => { e.preventDefault(); e.stopPropagation() }}>
              <button
                onClick={() => { deleteProject({ projectId: project._id }); setConfirmDeleteId(null) }}
                className="text-[10px] font-medium px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Yes
              </button>
              <button onClick={() => setConfirmDeleteId(null)} className="text-[10px] font-medium px-2 py-1 rounded bg-neutral-200 text-neutral-600 hover:bg-neutral-300 transition-colors">No</button>
            </div>
          ) : (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDeleteId(project._id) }}
              className="p-1 rounded text-neutral-300 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          <ChevronRightSmall className="h-4 w-4 text-neutral-300 mt-auto" />
        </div>
      </div>

      {/* Feedback row */}
      {feedback?.feedback && (
        <div className="border-t border-neutral-100 px-4 py-2.5 ml-[156px]">
          <div className="flex items-start gap-2">
            <div className="flex items-center gap-0.5 shrink-0 mt-px">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={11} className={s <= feedback.rating ? 'fill-amber-400 text-amber-400' : 'text-neutral-300'} />
              ))}
            </div>
            <p className="text-[11px] text-neutral-600 leading-relaxed line-clamp-2">{feedback.feedback}</p>
          </div>
        </div>
      )}
    </Link>
  )
}

function ProjectsTab() {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setSearch(value), 250)
  }, [])
  useEffect(() => () => clearTimeout(debounceRef.current), [])
  const [filter, setFilter] = useState<Filter>('hasExported')
  const [statusFilter, setStatusFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [userFilter, setUserFilter] = useState<{ userId: string; label: string } | null>(null)
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const deleteProject = useMutation(api.admin.deleteProject)

  const { results: projects, status, loadMore } = usePaginatedQuery(
    api.admin.listProjectsPaginated,
    {
      search: search || undefined,
      filter,
      statusFilter: statusFilter || undefined,
      sourceFilter: sourceFilter || undefined,
      planFilter: planFilter || undefined,
      userId: userFilter?.userId || undefined,
      sortOrder,
    },
    { initialNumItems: 20 },
  )
  const feedbackRatings = useQuery(api.exportFeedback.ratingsMap)
  const isLoading = status === 'LoadingFirstPage'
  const canLoadMore = status === 'CanLoadMore'
  const isLoadingMore = status === 'LoadingMore'

  const activeFilterCount = [statusFilter, sourceFilter, planFilter, userFilter].filter(Boolean).length
    + (filter !== 'all' && filter !== 'hasExported' ? 1 : 0)

  const handleUserFilter = (userId: string) => {
    const proj = projects.find((p: any) => p.userId === userId)
    const label = proj?.userName || proj?.userEmail || userId
    setUserFilter({ userId, label })
  }

  const clearAllFilters = () => {
    setFilter('all')
    setStatusFilter('')
    setSourceFilter('')
    setPlanFilter('')
    setUserFilter(null)
  }

  return (
    <div className="space-y-4">
      {/* Search + Filter bar */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search by title, email, name, or shortcode..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
            />
          </div>
          <div className="h-5 w-px bg-neutral-200" />
          <FilterSelect value={filter} onChange={(v) => setFilter(v as Filter)} options={CONTENT_FILTERS} label="Content filter" />
          <FilterSelect value={statusFilter} onChange={(v) => setStatusFilter(v)} options={STATUS_OPTIONS} label="Status" />
          <FilterSelect value={sourceFilter} onChange={(v) => setSourceFilter(v)} options={SOURCE_OPTIONS} label="Source" />
          <FilterSelect value={planFilter} onChange={(v) => setPlanFilter(v)} options={PLAN_OPTIONS} label="Plan" />
          <div className="h-5 w-px bg-neutral-200" />
          <button
            onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
            className="flex items-center gap-1 text-xs px-2 py-1.5 border border-neutral-200 rounded-lg bg-white text-neutral-500 hover:bg-neutral-50 transition-colors"
            title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
          >
            <ArrowUpDown className="h-3 w-3" />
            {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
          </button>
        </div>

        {/* Active filters display */}
        {(activeFilterCount > 0 || userFilter) && (
          <div className="flex items-center gap-2 flex-wrap">
            <FilterIcon className="h-3 w-3 text-neutral-400" />
            {filter !== 'all' && filter !== 'hasExported' && (
              <ActiveFilterPill
                label={CONTENT_FILTERS.find(f => f.value === filter)?.label || filter}
                onClear={() => setFilter('all')}
              />
            )}
            {statusFilter && (
              <ActiveFilterPill
                label={`Status: ${STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || statusFilter}`}
                onClear={() => setStatusFilter('')}
              />
            )}
            {sourceFilter && (
              <ActiveFilterPill
                label={`Source: ${SOURCE_OPTIONS.find(o => o.value === sourceFilter)?.label || sourceFilter}`}
                onClear={() => setSourceFilter('')}
              />
            )}
            {planFilter && (
              <ActiveFilterPill
                label={`Plan: ${PLAN_OPTIONS.find(o => o.value === planFilter)?.label || planFilter}`}
                onClear={() => setPlanFilter('')}
              />
            )}
            {userFilter && (
              <ActiveFilterPill
                label={`User: ${userFilter.label}`}
                onClear={() => setUserFilter(null)}
              />
            )}
            <button onClick={clearAllFilters} className="text-[11px] text-neutral-400 hover:text-neutral-600 ml-1">
              Clear all
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
        </div>
      ) : projects.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-12">
          No projects found
        </p>
      ) : (
        <div className="space-y-2.5">
          {projects.map((project: any) => (
            <ProjectCard
              key={project._id}
              project={project}
              confirmDeleteId={confirmDeleteId}
              setConfirmDeleteId={setConfirmDeleteId}
              deleteProject={deleteProject}
              feedback={feedbackRatings?.[project._id]}
              onUserClick={handleUserFilter}
            />
          ))}
        </div>
      )}

      {projects.length > 0 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-neutral-500 tabular-nums">
            Showing {projects.length} project{projects.length !== 1 ? 's' : ''}
            {status === 'Exhausted' ? ' (all loaded)' : ''}
          </p>
          {(canLoadMore || isLoadingMore) && (
            <Button variant="outline" size="sm" onClick={() => loadMore(20)} disabled={isLoadingMore}>
              {isLoadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Load more
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main Page ──────────────────────────────────────────────────────────────────

export default function ModToolsProjectsPage() {
  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-neutral-900">Videos</h1>
        <p className="text-sm text-neutral-500">Manage workflows</p>
      </div>
      <ProjectsTab />
    </div>
  )
}
